import React, { useState } from 'react'
import { ImageGridLayout, RichTextRenderer } from '../../../lib/components'
import { CMSOpportunityDetailsAdditionalSectionsType } from '../../../lib/types'
import { CMSSectionShell } from './cms-section-shell'
import { OpportunityExpandableSection } from './opportunity-expandable-section'
import { CmsSectionText } from './cms-section-text-content'

const CMSAdditionalSections = ({
  additionalSections,
  enableCollapsibleSections = false,
}: {
  additionalSections?: CMSOpportunityDetailsAdditionalSectionsType
  enableCollapsibleSections?: boolean
}) => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})

  const renderAdditionalSections = () => {
    const sectionsToRender = additionalSections?.sections?.filter((section) => section.showSection)
    const content: JSX.Element[] = []

    sectionsToRender?.forEach((section, idx) => {
      const sectionKey = `additional-section-${section?.sectionTitle}-${idx}`
      const sectionBody = (
        <>
          {section?.sectionVariant === 'variant-1' && (
            <>
              {section?.sectionText?.length && (
                <CmsSectionText
                  dataTestId={`cms-additional-section-text-${idx}`}
                  key={`cms-additional-section-text-${idx}`}
                >
                  <RichTextRenderer content={section.sectionText} variant='opportunity-details' />
                </CmsSectionText>
              )}
              <ImageGridLayout
                images={section?.images}
                layoutColumns={1}
                dataTestId={`cms-additional-section-images-${idx}`}
                key={`cms-additional-section-images-${idx}`}
                splitMiddleColumn={false}
                layoutContainerClasses='opportunity-cms-section-image-layout'
                imageClasses='width-80percent'
              />
            </>
          )}
          {section?.sectionVariant === 'variant-2' && (
            <section
              className={`flex gap-10px ${
                section?.imagePosition === 'left' ? 'flex-row-reverse' : ''
              }`}
              data-testid={`cms-additional-section-content-${idx}`}
              key={`cms-additional-section-content-${idx}`}
            >
              {section?.sectionText?.length && (
                <CmsSectionText className='card flex-1'>
                  <RichTextRenderer content={section.sectionText} variant='opportunity-details' />
                </CmsSectionText>
              )}
              {section?.image && (
                <section className='card card-image'>
                  <img
                    src={section.image}
                    alt={`${section.sectionTitle} image`}
                    className='width-100percent height-100percent'
                  />
                </section>
              )}
            </section>
          )}
        </>
      )

      if (enableCollapsibleSections) {
        const isOpen = openSections[sectionKey] ?? section.expandSection === true
        content.push(
          <OpportunityExpandableSection
            key={`additional-section-wrapper-${sectionKey}`}
            id={`opportunity-expandable-${sectionKey}`}
            title={section?.sectionTitle || 'Details'}
            backgroundColor={section?.backgroundColor?.hex}
            isOpen={isOpen}
            onToggle={() => setOpenSections((prev) => ({ ...prev, [sectionKey]: !isOpen }))}
            sectionTestId={`cms-additional-section-${section?.sectionTitle}-${idx}`}
          >
            {sectionBody}
          </OpportunityExpandableSection>,
        )
        return
      }

      content.push(
        <CMSSectionShell
          key={`additional-section-wrapper-${sectionKey}`}
          id={sectionKey}
          title={section?.sectionTitle}
          backgroundColor={section?.backgroundColor?.hex}
          titleTestId={`cms-additional-section-${section?.sectionTitle}-${idx}`}
        >
          {sectionBody}
        </CMSSectionShell>,
      )
    })

    return content
  }

  return (
    <>
      {additionalSections?.header?.trim() ? (
        <CMSSectionShell
          id='opportunity-section-additional-sections-header'
          title={additionalSections.header}
          backgroundColor={additionalSections.backgroundColor?.hex}
          titleTestId='cms-additional-sections-heading'
        />
      ) : null}
      {renderAdditionalSections()}
    </>
  )
}

export { CMSAdditionalSections }
